import AudioWaveform from '@/components/AudioWaveform';
import GlassCard from '@/components/ui/glass-card';
import { useRecordingContext } from '@/contexts/RecordingContext';
import { useToast } from '@/hooks/use-toast';
import { AiScribeCompleteResponse } from '@/lib/ai-scribe-service';
import { fadeIn } from '@/lib/animations';
import { getAudioUrl } from '@/lib/audio-utils';
import { gatewayService } from '@/lib/gateway-service';
import { AuthStateSelector, RecordingStateSelector } from '@/store/selectors';
import { updateAppointmentScribeJobId } from '@/store/slices/dataSlice';
import {
  pauseRecording,
  resetRecording,
  resumeRecording,
} from '@/store/slices/recordingSlice';
import { type SimplifiedAppointment } from '@/types/appointment';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Mic,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Square,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from './ui/button';

/**
 * Formats an ISO date string to MM/dd/yyyy HH:mm:ss format in UTC
 * @param isoDateString - Date string in ISO format from integration partner (UTC)
 * @returns Formatted date string in MM/dd/yyyy HH:mm:ss format (UTC)
 */
const formatAppointmentStartTime = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString);

    // Use UTC methods directly since appointmentStartTime is already in UTC
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('Error formatting appointment start time:', error);
    // Return current UTC time as fallback
    const now = new Date();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const year = now.getUTCFullYear();
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  }
};

/**
 * Formats a date string to MM/dd/yyyy format using UTC methods to preserve the exact date
 * @param dateString - Date string that can be parsed by Date constructor
 * @returns Formatted date string in MM/dd/yyyy format (preserves original date)
 */
const formatDateOfBirth = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string provided:', dateString);
      return dateString; // Return original if invalid
    }

    // Use UTC methods to preserve the exact date as received from API
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date of birth:', error);
    return dateString; // Return original string if parsing fails
  }
};

// Streaming-Friendly Waveform Component
const StreamingWaveform = ({
  audioUrl,
  currentTime,
  duration,
  onSeek,
}: {
  audioUrl: string;
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
}) => {
  const [waveformData, setWaveformData] = useState<number[]>([]);

  useEffect(() => {
    // Generate realistic-looking waveform without downloading audio
    const generateRealisticWaveform = () => {
      const samples = 250;
      const amplitudes: number[] = [];

      for (let i = 0; i < samples; i++) {
        // Create natural-looking audio patterns
        const baseFreq = 0.16;
        const harmonic1 = 0.5;
        const harmonic2 = 0.25;

        // Mix of different frequencies for realistic speech patterns
        const wave1 = Math.sin(i * baseFreq) * 0.4;
        const wave2 = Math.sin(i * harmonic1) * 0.3;
        const wave3 = Math.sin(i * harmonic2) * 0.2;

        // Add some randomness for natural variation
        const noise = (Math.random() - 0.5) * 0.3;

        // Combine waves and normalize
        let amplitude = wave1 + wave2 + wave3 + noise;

        // Create some natural gaps (silence periods)
        if (i % 25 === 0 || i % 37 === 0) {
          amplitude *= 0.1; // Quiet periods
        }

        // Occasional peaks for emphasis
        if (i % 15 === 7 || i % 22 === 12) {
          amplitude *= 1.3; // Louder periods
        }

        // Ensure amplitude is within 0-1 range
        amplitude = Math.max(0, Math.min(1, (amplitude + 1) / 2));

        amplitudes.push(amplitude);
      }

      setWaveformData(amplitudes);
    };

    if (audioUrl) {
      generateRealisticWaveform();
    }
  }, [audioUrl]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    onSeek(newTime);
  };

  return (
    <div
      className="relative w-full h-10 rounded-md border border-gray-400/20 dark:border-white/10 cursor-pointer overflow-hidden group backdrop-blur-sm shadow-inner hidden xl:block"
      onClick={handleClick}>
      {/* Subtle inner glow */}
      <div className="absolute bg-gradient-to-r from-purple-500/5 via-transparent to-blue-500/5 rounded-xl" />

      {/* Waveform bars - Elegant & Full Width */}
      <div className="flex items-center h-full w-full px-1 gap-[1px] hidden md:flex">
        {waveformData.map((amplitude, index) => {
          const isPlayed =
            duration > 0
              ? index / waveformData.length <= currentTime / duration
              : false;
          const height = Math.max(amplitude * 90, 1);

          return (
            <div
              key={index}
              className={`flex-1 rounded-md transition-all duration-150 ease-out ${
                isPlayed
                  ? 'bg-blue-500/90 shadow-sm'
                  : 'bg-gradient-to-t from-slate-400/50 to-slate-500/50 dark:from-slate-600/50 dark:to-slate-500/50'
              }`}
              style={{ height: `${height}%` }}
            />
          );
        })}
      </div>

      {/* Progress cursor - Ultra Smooth */}
      {duration > 0 && (
        <div
          className="absolute top-0 h-full bg-red-500 rounded-full shadow-lg hidden md:block"
          style={{ left: `${(currentTime / duration) * 100}%`, width: '3px' }}
        />
      )}

      {/* Hover indicator */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-transparent to-blue-500/10 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:block" />
    </div>
  );
};

// Enhanced Control Button Component
const ControlButton = ({
  onClick,
  variant = 'default',
  size = 'default',
  className = '',
  children,
  disabled = false,
}: {
  onClick: () => void;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
}) => {
  const variants = {
    default: {
      bg: 'bg-white/75 hover:bg-white/90 dark:bg-slate-800/75 dark:hover:bg-slate-700/90',
      border: 'border-gray-400/40 dark:border-white/20',
      text: 'text-slate-700 dark:text-slate-200',
      shadow: 'shadow-sm hover:shadow-md',
    },
    primary: {
      bg: 'bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800',
      border: 'border-purple-500/40',
      text: 'text-white',
      shadow: 'shadow-sm hover:shadow-md shadow-purple-500/20',
    },
    success: {
      bg: 'bg-gradient-to-r from-emerald-500/85 to-emerald-600/85 hover:from-emerald-500 hover:to-emerald-600',
      border: 'border-emerald-400/40',
      text: 'text-white',
      shadow: 'shadow-sm hover:shadow-md shadow-emerald-500/20',
    },
    warning: {
      bg: 'bg-gradient-to-r from-orange-500/85 to-orange-600/85 hover:from-orange-500 hover:to-orange-600',
      border: 'border-orange-400/40',
      text: 'text-white',
      shadow: 'shadow-sm hover:shadow-md shadow-orange-500/20',
    },
    danger: {
      bg: 'bg-gradient-to-r from-red-500/85 to-red-600/85 hover:from-red-500 hover:to-red-600',
      border: 'border-red-400/40',
      text: 'text-white',
      shadow: 'shadow-sm hover:shadow-md shadow-red-500/20',
    },
  };

  const sizes = {
    sm: 'h-8 w-8 p-0',
    default: 'h-10 w-10 p-0',
    lg: 'h-12 w-12 p-0',
  };

  const style = variants[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        relative group ${style.bg} ${sizes[size]} ${className}
        rounded-3xl border ${style.border} backdrop-blur-md
        transition-all duration-200 ease-out
        ${style.shadow}
        flex items-center justify-center
        disabled:opacity-50 disabled:cursor-not-allowed
        overflow-hidden
        focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500/40
      `}>
      {/* Content */}
      <div className={`relative z-10 ${style.text}`}>{children}</div>
    </button>
  );
};

// Simple Audio Player Component
const SimpleAudioPlayer = ({
  audioUrl,
  isPlaying,
  onPlayPause,
  onTimeUpdate,
  isMuted,
  onMuteToggle,
  onPlayStateChange,
  onAudioRef,
  isVisible = true,
}: {
  audioUrl: string;
  isPlaying: boolean;
  onPlayPause: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  isMuted: boolean;
  onMuteToggle: () => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onAudioRef?: (audioElement: HTMLAudioElement | null) => void;
  isVisible?: boolean;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const animationFrameRef = useRef<number>();

  // Smooth cursor update using requestAnimationFrame
  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused && !audio.ended) {
      setCurrentTime(audio.currentTime);
      onTimeUpdate?.(audio.currentTime, audio.duration);
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }
  }, [onTimeUpdate]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Expose audio element to parent component
    onAudioRef?.(audio);

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleLoadedMetadata = () => setDuration(audio.duration);

    // Use timeupdate as backup, but rely on RAF for smooth updates
    const handleTimeUpdate = () => {
      if (audio.paused || audio.ended) {
        setCurrentTime(audio.currentTime);
        onTimeUpdate?.(audio.currentTime, audio.duration);
      }
    };

    const handlePlay = () => {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
      onPlayStateChange?.(true);
    };

    const handlePause = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setCurrentTime(audio.currentTime);
      onPlayStateChange?.(false);
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handlePause);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Clear audio ref when component unmounts
      onAudioRef?.(null);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handlePause);
    };
  }, [updateProgress, onTimeUpdate, onAudioRef]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.play().catch(console.error);
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  return (
    <>
      {isVisible && <audio ref={audioRef} src={audioUrl} preload="metadata" />}
      <StreamingWaveform
        audioUrl={audioUrl}
        currentTime={currentTime}
        duration={duration}
        onSeek={(time) => {
          if (audioRef.current) {
            // Immediate visual update for responsiveness
            setCurrentTime(time);
            audioRef.current.currentTime = time;
          }
        }}
      />
    </>
  );
};

interface AiScribeAudioControlsProps {
  selectedAppointment: SimplifiedAppointment | null;
  jobDetails: any;
  aiScribeLoading: boolean;
  selectedLocationId?: string;
  isTranscriptVisible: boolean;
  isLiveTranscriptVisible: boolean;
  onToggleTranscript: () => void;
  onAudioPlayStateChange?: (isPlaying: boolean) => void;
  externalIsPlaying?: boolean;
  onRecordingStopped?: (response: AiScribeCompleteResponse | void) => void;
  onAudioElementChange?: (audioElement: HTMLAudioElement | null) => void;
  isVisible?: boolean;
}

const AiScribeAudioControls = ({
  selectedAppointment,
  jobDetails,
  aiScribeLoading,
  selectedLocationId,
  isTranscriptVisible,
  isLiveTranscriptVisible,
  onToggleTranscript,
  onAudioPlayStateChange,
  externalIsPlaying,
  onRecordingStopped,
  onAudioElementChange,
  isVisible = true,
}: AiScribeAudioControlsProps) => {
  const dispatch = useDispatch();
  const user = useSelector(AuthStateSelector.user);
  const recordingError = useSelector(RecordingStateSelector.error);
  const isStopped = useSelector(RecordingStateSelector.isStopped);

  const isRecording = useSelector(RecordingStateSelector.isRecording);
  const isPaused = useSelector(RecordingStateSelector.isPaused);
  const isPausedFromDatabase = useSelector(
    RecordingStateSelector.isPausedFromDatabase
  );
  const { toast } = useToast();

  // Get recording state from context
  const {
    startRecording,
    stopRecording: stopRecordingService,
    pauseRecording: pauseRecordingService,
    resumeRecording: resumeRecordingService,
  } = useRecordingContext();

  // Playback state - use external state if provided, otherwise use internal state
  const [internalIsPlaying, setInternalIsPlaying] = useState(false);
  const isPlaying =
    externalIsPlaying !== undefined ? externalIsPlaying : internalIsPlaying;
  const setIsPlaying =
    externalIsPlaying !== undefined
      ? onAudioPlayStateChange || (() => {})
      : setInternalIsPlaying;
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState('1x');
  const [isPreparing, setIsPreparing] = useState(false);

  // Track current audio element to avoid DOM queries
  const [currentAudioElement, setCurrentAudioElement] =
    useState<HTMLAudioElement | null>(null);

  // Callback to handle audio element changes
  const handleAudioElementChange = useCallback(
    (audioElement: HTMLAudioElement | null) => {
      setCurrentAudioElement(audioElement);
      onAudioElementChange?.(audioElement);
    },
    [onAudioElementChange]
  );

  // Recording control handlers
  const handleStartRecording = useCallback(async () => {
    const errorMessage =
      validateAppointmentBeforeRecording(selectedAppointment);
    if (errorMessage) {
      console.error('Validation error:', errorMessage);
      toast({
        title: 'Validation Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return;
    }

    console.log(
      '🎙️ Starting recording for appointment:',
      selectedAppointment.appointmentId
    );

    // Prepare IDs
    const appointmentId = selectedAppointment.appointmentId || '';
    const patientId = selectedAppointment.patientId || '';
    // Use selectedLocationId instead of appointment's locationId (which might be empty)
    const locationId =
      selectedLocationId || selectedAppointment.locationId || '';
    const createdBy = user.id;

    // Determine correct partNumber - get from Redux store if continuing an existing job
    let partNumber = 1; // Default for new recordings
    if (selectedAppointment.scribeJobId && selectedAppointment.parts) {
      if (selectedAppointment.parts.length > 0) {
        const maxPartNumber = Math.max(
          ...selectedAppointment.parts.map((part: any) => part.partNumber)
        );
        partNumber = maxPartNumber + 1;
        console.log(
          `✅ Found ${selectedAppointment.parts.length} existing parts in Redux. Using partNumber: ${partNumber}`
        );
      } else {
        console.log('✅ No existing parts in Redux. Using partNumber: 1');
      }
    } else if (selectedAppointment.scribeJobId) {
      // Fallback: If parts not in Redux, fetch from API
      try {
        console.log(
          '📊 Parts not in Redux, fetching job details for partNumber...'
        );
        const jobDetailsResult = await gatewayService.getJobDetailsByJobId(
          selectedAppointment.scribeJobId
        );

        if (jobDetailsResult.success && jobDetailsResult.data) {
          const jobData = jobDetailsResult.data as any;
          if (jobData.job?.parts && jobData.job.parts.length > 0) {
            const maxPartNumber = Math.max(
              ...jobData.job.parts.map((part: any) => part.partNumber)
            );
            partNumber = maxPartNumber + 1;
            console.log(
              `✅ Found ${jobData.job.parts.length} existing parts from API. Using partNumber: ${partNumber}`
            );
          } else {
            console.log('✅ No existing parts found. Using partNumber: 1');
          }
        }
      } catch (error) {
        console.warn(
          '⚠️ Failed to fetch job details for partNumber. Using default: 1',
          error
        );
        partNumber = 1;
      }
    }

    // UX-first: start recording immediately; create job in parallel.
    try {
      await startRecording(
        appointmentId,
        patientId,
        locationId,
        partNumber,
        createdBy
      );
    } catch (error) {
      console.error('Failed to start recording:', error);
      return;
    }

    // Background job creation; UploadScheduler will defer uploads until scribeJobId exists
    (async () => {
      try {
        setIsPreparing(true);
        let scribeJobId = selectedAppointment.scribeJobId;
        if (!scribeJobId) {
          const cloudFunctionResult = await gatewayService.createScribeJob({
            appointmentId: appointmentId.toString(),
            locationId: locationId.toString(),
            patientId: patientId.toString(),
            firstName: selectedAppointment.patientFirstName || '',
            lastName: selectedAppointment.patientLastName || '',
            dob: formatDateOfBirth(
              selectedAppointment.patientBirthDate || ''
            ),
            gender: selectedAppointment.patientGender || '',
            service: selectedAppointment.service || '',
            appointmentStartTime: formatAppointmentStartTime(
              selectedAppointment.startTime || ''
            ),
          });

          if (cloudFunctionResult.success && cloudFunctionResult.data) {
            const responseData = cloudFunctionResult.data as {
              scribeJobId: string;
            };
            if (responseData.scribeJobId) {
              dispatch(
                updateAppointmentScribeJobId({
                  appointmentId: selectedAppointment.appointmentId,
                  scribeJobId: responseData.scribeJobId,
                })
              );
              console.log(
                '✅ Updated appointment with scribeJobId:',
                responseData.scribeJobId
              );
            } else {
              console.warn('⚠️ Cloud function response missing scribeJobId');
            }
          } else {
            console.error(
              '❌ Failed to create AI Scribe job:',
              cloudFunctionResult.error
            );
          }
        }
      } catch (error) {
        console.error(
          'Failed to call createAiScribeJob cloud function:',
          error
        );
      } finally {
        setIsPreparing(false);
      }
    })();
  }, [
    selectedAppointment,
    user?.id,
    selectedLocationId,
    dispatch,
    startRecording,
  ]);

  function validateAppointmentBeforeRecording(
    appointment: SimplifiedAppointment
  ): string {
    let errorMessage: string = '';
    if (!appointment || !user?.id) {
      errorMessage = 'No appointment selected or user not logged in';
      return errorMessage;
    }
    if (!appointment.patientId) {
      errorMessage = 'Patient ID is required';
      return errorMessage;
    }
    if (appointment.patientFirstName === '') {
      errorMessage = 'Patient first name is required';
      return errorMessage;
    }
    if (appointment.patientLastName === '') {
      errorMessage = 'Patient last name is required';
      return errorMessage;
    }
    if (appointment.patientBirthDate === '') {
      errorMessage = 'Patient birth date is required';
      return errorMessage;
    }
    if (appointment.patientGender === '') {
      errorMessage = 'Patient gender is required';
      return errorMessage;
    }
    if (appointment.service === '') {
      errorMessage = 'Service is required';
      return errorMessage;
    }
    return errorMessage;
  }

  const handleStopRecording = useCallback(async () => {
    if (!selectedAppointment || !user?.id) return;
    try {
      const patientId = selectedAppointment.patientId || '';
      const result = await stopRecordingService(
        patientId,
        user.id,
        true // shouldCallAPI
      );
      console.log(
        '✅ stopRecordingService API call completed successfully.',
        result
      );

      // If the call was successful, and we have a result, notify the parent
      if (result) {
        onRecordingStopped?.(result);
      }
    } catch (error) {
      console.error('Failed to stop recording:', error);
    }
  }, [
    selectedAppointment,
    user?.id,
    dispatch,
    stopRecordingService,
    onRecordingStopped,
  ]);

  const handlePauseRecording = useCallback(async () => {
    if (!selectedAppointment) return;

    try {
      const patientId = selectedAppointment.patientId || '';

      await pauseRecordingService(patientId);
      dispatch(pauseRecording());
    } catch (error) {
      console.error('Failed to pause recording:', error);
    }
  }, [selectedAppointment, dispatch, pauseRecordingService]);

  const handleResumeRecording = useCallback(async () => {
    if (!selectedAppointment) {
      console.error('No appointment selected');
      return;
    }

    try {
      const patientId = selectedAppointment.patientId || '';

      await resumeRecordingService(patientId);
      dispatch(resumeRecording());
    } catch (error) {
      console.error('Failed to resume recording:', error);
    }
  }, [selectedAppointment, dispatch, resumeRecordingService]);

  const handleResetRecording = useCallback(() => {
    dispatch(resetRecording());
  }, [dispatch]);

  if (!selectedAppointment) {
    return null;
  }

  return (
    <motion.div
      className="w-full"
      variants={fadeIn}
      initial="initial"
      animate="animate">
      {/* Enhanced container with compact spacing */}
      <GlassCard className="w-full" variant="elevated">
        {/* Recording Error with enhanced styling */}
        {recordingError && (
          <div>
            <div className="relative bg-gradient-to-r from-red-500/10 to-red-400/10 border border-red-500/25 rounded-2xl p-3 backdrop-blur-lg">
              {/* Inner glass layers */}
              <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-br from-white/20 via-white/10 to-white/2 dark:from-white/10 dark:via-white/5 dark:to-transparent opacity-60" />

              <p className="relative z-10 text-red-600 dark:text-red-400 text-sm font-semibold">
                {recordingError}
              </p>
            </div>
          </div>
        )}

        {/* Audio Controls with reduced spacing */}
        <div className="p-0">
          {/* Main Controls Row with compact spacing */}
          <div className="flex items-center gap-3">
            {(() => {
              const hasAudioFile =
                selectedAppointment?.audioUrl || jobDetails?.job?.audioFileUrl;
              const hasAudio =
                hasAudioFile ||
                jobDetails?.job?.audioFileKey ||
                (isStopped && getAudioUrl());

              // Recording Mode
              if (isRecording || isPaused) {
                return (
                  <>
                    {/* Recording Controls */}
                    <div className="flex items-center gap-2">
                      {isRecording && !isPaused && (
                        <>
                          <ControlButton
                            onClick={handlePauseRecording}
                            variant="warning"
                            size="default">
                            <Pause className="w-5 h-5" />
                          </ControlButton>
                          <ControlButton
                            onClick={handleStopRecording}
                            variant="danger"
                            size="default">
                            <Square className="w-5 h-5" />
                          </ControlButton>
                        </>
                      )}
                      {isPaused && (
                        <>
                          <ControlButton
                            onClick={handleResumeRecording}
                            variant="success"
                            size="default">
                            <Mic className="w-5 h-5" />
                          </ControlButton>
                          <ControlButton
                            onClick={handleStopRecording}
                            variant="danger"
                            size="default">
                            <Square className="w-5 h-5" />
                          </ControlButton>
                        </>
                      )}
                    </div>
                    {/* Enhanced Waveform */}
                    <div className="flex-1">
                      <AudioWaveform
                        height={32}
                        barWidth={2}
                        barGap={1}
                        backgroundColor="rgba(0, 0, 0, 0.05)"
                        silenceColor="#6B7280"
                      />
                    </div>
                  </>
                );
              }

              // Audio Playback Mode
              if (hasAudio) {
                return (
                  <>
                    {/* Playback Controls - Left Side */}
                    <div className="flex items-center gap-2">
                      {/* Skip Backward */}
                      <ControlButton
                        onClick={() => {
                          if (
                            currentAudioElement &&
                            currentAudioElement.duration
                          ) {
                            const newTime = Math.max(
                              0,
                              currentAudioElement.currentTime - 5
                            );
                            currentAudioElement.currentTime = newTime;
                          }
                        }}
                        size="sm">
                        <RotateCcw className="w-4 h-4" />
                      </ControlButton>

                      {/* Play/Pause - Enhanced primary button */}
                      <ControlButton
                        onClick={() => setIsPlaying(!isPlaying)}
                        variant="primary"
                        size="lg">
                        {isPlaying ? (
                          <Pause className="w-6 h-6" />
                        ) : (
                          <Play className="w-6 h-6" />
                        )}
                      </ControlButton>

                      {/* Skip Forward */}
                      <ControlButton
                        onClick={() => {
                          if (
                            currentAudioElement &&
                            currentAudioElement.duration
                          ) {
                            const newTime = Math.min(
                              currentAudioElement.duration,
                              currentAudioElement.currentTime + 5
                            );
                            currentAudioElement.currentTime = newTime;
                          }
                        }}
                        size="sm">
                        <RotateCw className="w-4 h-4" />
                      </ControlButton>

                      {/* Volume */}
                      <ControlButton
                        onClick={() => setIsMuted(!isMuted)}
                        size="sm">
                        {isMuted ? (
                          <VolumeX className="w-4 h-4" />
                        ) : (
                          <Volume2 className="w-4 h-4" />
                        )}
                      </ControlButton>

                      {/* Enhanced Speed Dropdown */}
                      <select
                        value={playbackSpeed}
                        onChange={(e) => {
                          const newSpeed = e.target.value;
                          setPlaybackSpeed(newSpeed);
                          if (currentAudioElement) {
                            currentAudioElement.playbackRate = parseFloat(
                              newSpeed.replace('x', '')
                            );
                          }
                        }}
                        className="h-8 px-2 text-sm bg-white/75 hover:bg-white/90 dark:bg-slate-800/75 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500/40 transition-all duration-200 backdrop-blur-md shadow-sm hover:shadow-md font-medium cursor-pointer">
                        <option value="0.25x">0.25x</option>
                        <option value="0.5x">0.5x</option>
                        <option value="1x">1.0x</option>
                        <option value="1.25x">1.25x</option>
                        <option value="1.5x">1.5x</option>
                        <option value="2x">2.0x</option>
                      </select>
                    </div>

                    {/* Waveform - Center (Full Width) */}
                    <div className="flex-1 flex items-center">
                      <SimpleAudioPlayer
                        audioUrl={(() => {
                          // Prioritize audioUrl from the appointment (from complete recording response)
                          if (selectedAppointment?.audioUrl) {
                            return selectedAppointment.audioUrl;
                          }
                          if (jobDetails?.job?.audioFileUrl) {
                            return jobDetails.job.audioFileUrl;
                          }
                          if (jobDetails?.job?.audioFileKey) {
                            return getAudioUrl(jobDetails.job.audioFileKey);
                          }
                          return '';
                        })()}
                        isPlaying={isPlaying}
                        onPlayPause={() => {
                          const newState = !isPlaying;
                          setIsPlaying(newState);
                          onAudioPlayStateChange?.(newState);
                        }}
                        isMuted={isMuted}
                        onMuteToggle={() => setIsMuted(!isMuted)}
                        onPlayStateChange={onAudioPlayStateChange}
                        onAudioRef={handleAudioElementChange}
                        isVisible={isVisible}
                      />
                    </div>

                    {/* Enhanced Transcript Toggle Button */}
                    {(() => {
                      const hasTranscriptUrl =
                        jobDetails?.job?.transcriptFileUrl;
                      const hasLiveTranscript = isLiveTranscriptVisible;

                      if (
                        isLiveTranscriptVisible ||
                        (hasTranscriptUrl && hasTranscriptUrl.trim())
                      ) {
                        return (
                          <Button
                            onClick={onToggleTranscript}
                            className={`
                                relative h-8 px-3 text-sm transition-all duration-200 rounded-3xl border border-gray-400/40 dark:border-white/20 backdrop-blur-md shadow-sm hover:shadow-md flex items-center gap-1 overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500/40 bg-gray-400/20 hover:bg-gray-400/30 dark:bg-slate-800/75 dark:hover:bg-slate-700/90 text-slate-700 dark:text-slate-200 hidden md:flex
                                
                              `}>
                            {/* Subtle glass layer */}
                            <div className="absolute inset-[1px] rounded-lg bg-gradient-to-br from-white/20 via-white/10 to-white/5 dark:from-white/10 dark:via-white/5 dark:to-transparent" />

                            <div className="relative z-10 flex items-center gap-1">
                              {isTranscriptVisible ? (
                                <>
                                  <ChevronLeft className="w-4 h-4" />
                                  Hide Transcript
                                </>
                              ) : (
                                <>
                                  Show Transcript
                                  <ChevronLeft className="w-4 h-4 rotate-180" />
                                </>
                              )}
                            </div>
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </>
                );
              }

              // Initial State - Enhanced Start Recording
              return (
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    {/* Enhanced Start Recording Button - compact */}
                    <button
                      onClick={handleStartRecording}
                      disabled={isPreparing}
                      className={`relative bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-600 hover:to-blue-900 text-white font-semibold py-2.5 px-5 rounded-3xl text-sm transition-all duration-200 flex items-center gap-2 border border-purple-400/40 backdrop-blur-md overflow-hidden shadow-sm hover:shadow-md shadow-purple-500/20 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500/40 xs:w-full ${
                        isPreparing ? 'opacity-70 cursor-not-allowed' : ''
                      }`}>
                      {/* Subtle glass layer */}
                      <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-white/20 via-white/10 to-white/5" />
                      <div className="relative z-10 flex items-center gap-2">
                        <Mic className="w-4 h-4 text-white" />
                        <span className="tracking-wide">
                          {isPreparing
                            ? 'Preparing…'
                            : isPausedFromDatabase
                            ? 'Resume Recording'
                            : 'Start Recording'}
                        </span>
                      </div>
                    </button>

                    {/* Enhanced Ready State Display */}
                    <div className="flex-1 relative hidden md:block">
                      <div className="relative bg-gradient-to-r from-slate-50/60 via-white/80 to-slate-50/60 dark:from-slate-800/60 dark:via-slate-700/80 dark:to-slate-800/60 rounded-2xl border border-light-gray/50 dark:border-white/20 flex items-center justify-center h-10 backdrop-blur-lg shadow-inner">
                        {/* Inner glass layers */}
                        <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-br from-white/60 via-white/40 to-white/5 dark:from-white/20 dark:via-white/10 dark:to-transparent opacity-60" />
                      </div>
                    </div>

                    {/* Transcript Button for Initial State */}
                    {(() => {
                      const hasTranscriptUrl =
                        jobDetails?.job?.transcriptFileUrl;

                      if (hasTranscriptUrl && hasTranscriptUrl.trim()) {
                        return (
                          <button
                            onClick={onToggleTranscript}
                            className={`
                                relative h-8 px-3 font-medium text-xs transition-all duration-200 rounded-lg border backdrop-blur-md shadow-sm hover:shadow-md flex items-center gap-1 overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500/40 active:scale-[0.98] hover:scale-[1.02]
                                ${
                                  isTranscriptVisible
                                    ? 'bg-gradient-to-r from-purple-500/85 to-blue-500/85 border-purple-500/40 text-white shadow-purple-500/20'
                                    : 'bg-white/75 hover:bg-white/90 dark:bg-slate-800/75 dark:hover:bg-slate-700/90 border-white/40 dark:border-white/20 text-slate-700 dark:text-slate-200'
                                }
                              `}>
                            {/* Subtle glass layer */}
                            <div className="absolute inset-[1px] rounded-lg bg-gradient-to-br from-white/20 via-white/10 to-white/5 dark:from-white/10 dark:via-white/5 dark:to-transparent" />

                            <div className="relative z-10 flex items-center gap-1">
                              {isTranscriptVisible ? (
                                <>
                                  <ChevronLeft className="w-3 h-3" />
                                  Hide Transcript
                                </>
                              ) : (
                                <>
                                  Show Transcript
                                  <ChevronLeft className="w-3 h-3 rotate-180" />
                                </>
                              )}
                            </div>
                          </button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </GlassCard>
    </motion.div>
  );
};

export default AiScribeAudioControls;
